import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/layout/Layout";
import RouteEffects from "./components/layout/RouteEffects";
import { CMSSettingsProvider, useCMSSettings } from "./hooks/useCMSSettings";
import { InjectIntegrations } from "./components/InjectIntegrations";
import { CookieBanner } from "./components/CookieBanner";
import { MaintenancePage } from "./components/MaintenancePage";
import { HelmetProvider } from "react-helmet-async";
// Lazy-load cursor effects — desktop only, deferred until after hydration
const CustomCursor = lazy(() => import("./components/ui/CustomCursor").then(m => ({ default: m.CustomCursor })));
const MouseTrail = lazy(() => import("./components/effects/MouseTrail").then(m => ({ default: m.MouseTrail })));

// Only the homepage is eagerly loaded (LCP critical path)
import Index from "./pages/Index";

// All other pages are lazy-loaded to reduce initial bundle size (~250 KiB savings)
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const Careers = lazy(() => import("./pages/Careers"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Services = lazy(() => import("./pages/Services"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Uae = lazy(() => import("./pages/Uae"));
const Uk = lazy(() => import("./pages/Uk"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Service pages
const Analytics = lazy(() => import("./pages/services/Analytics"));
const AiConsulting = lazy(() => import("./pages/services/AiConsulting"));
const ApiDevelopment = lazy(() => import("./pages/services/ApiDevelopment"));
const WebDevelopment = lazy(() => import("./pages/services/WebDevelopment"));
const CrmDevelopment = lazy(() => import("./pages/services/CrmDevelopment"));
const ErpDevelopment = lazy(() => import("./pages/services/ErpDevelopment"));
const AiAgents = lazy(() => import("./pages/services/AiAgents"));
const AiCallCentre = lazy(() => import("./pages/services/AiCallCentre"));
const LlmIntegration = lazy(() => import("./pages/services/LlmIntegration"));
const Branding = lazy(() => import("./pages/services/Branding"));
const CloudDevOps = lazy(() => import("./pages/services/CloudDevOps"));
const Integrations = lazy(() => import("./pages/services/Integrations"));
const MobileApps = lazy(() => import("./pages/services/MobileApps"));
const RagPipelines = lazy(() => import("./pages/services/RagPipelines"));
const RpaAutomation = lazy(() => import("./pages/services/RpaAutomation"));
const Seo = lazy(() => import("./pages/services/Seo"));
const UiUx = lazy(() => import("./pages/services/UiUx"));
const AppDevelopment = lazy(() => import("./pages/services/AppDevelopment"));
const DigitalMarketing = lazy(() => import("./pages/services/DigitalMarketing"));
const BrandStrategy = lazy(() => import("./pages/services/BrandStrategy"));
const Shopify = lazy(() => import("./pages/services/Shopify"));
const CustomSoftware = lazy(() => import("./pages/services/CustomSoftware"));
const GraphicDesign = lazy(() => import("./pages/services/GraphicDesign"));
const ContentWriting = lazy(() => import("./pages/services/ContentWriting"));
const WordPress = lazy(() => import("./pages/services/WordPress"));
const CreativeServices = lazy(() => import("./pages/services/CreativeServices"));
const SEOServices = lazy(() => import("./pages/services/SEOServices"));
const DigitalStrategy = lazy(() => import("./pages/services/DigitalStrategy"));
const SocialMedia = lazy(() => import("./pages/services/SocialMedia"));
const ECommerce = lazy(() => import("./pages/services/ECommerce"));
const UIUXDesign = lazy(() => import("./pages/services/UIUXDesign"));
const WebApps = lazy(() => import("./pages/services/WebApps"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const LocationServicePage = lazy(() => import("./pages/LocationServicePage"));

// Work pages
const WorkIndex = lazy(() => import("./pages/work/WorkIndex"));
const WorkCaseStudy = lazy(() => import("./pages/work/WorkCaseStudy"));

// Industry pages
const IndustriesIndex = lazy(() => import("./pages/industries/IndustriesIndex"));
const Legal = lazy(() => import("./pages/industries/Legal"));
const RealEstate = lazy(() => import("./pages/industries/RealEstate"));
const Healthcare = lazy(() => import("./pages/industries/Healthcare"));
const Education = lazy(() => import("./pages/industries/Education"));
const Manufacturing = lazy(() => import("./pages/industries/Manufacturing"));

// Product pages
const ProductsIndex = lazy(() => import("./pages/products/ProductsIndex"));
const ProductDetail = lazy(() => import("./pages/products/ProductDetail"));

// Location pages
const Locations = lazy(() => import("./pages/Locations"));
const CountryLandingPage = lazy(() => import("./pages/CountryLandingPage"));
const CityLandingPage = lazy(() => import("./pages/CityLandingPage"));
const IndiaIndex = lazy(() => import("./pages/india/IndiaIndex"));
const Mumbai = lazy(() => import("./pages/india/Mumbai"));

// Other pages
const HTMLSitemap = lazy(() => import("./pages/HTMLSitemap"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));

// Admin — lazy loaded (not needed for public visitors)
const AdminLogin = lazy(() => import("./admin/Login").then(m => ({ default: m.AdminLogin })));
const AdminLayout = lazy(() => import("./admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./admin/index").then(m => ({ default: m.AdminDashboard })));
const AdminContact = lazy(() => import("./admin/index").then(m => ({ default: m.AdminContact })));
const AdminPages = lazy(() => import("./admin/index").then(m => ({ default: m.AdminPages })));
const AdminBlog = lazy(() => import("./admin/index").then(m => ({ default: m.AdminBlog })));
const AdminNav = lazy(() => import("./admin/index").then(m => ({ default: m.AdminNav })));
const AdminMedia = lazy(() => import("./admin/index").then(m => ({ default: m.AdminMedia })));
const AdminIntegrations = lazy(() => import("./admin/index").then(m => ({ default: m.AdminIntegrations })));
const AdminSettings = lazy(() => import("./admin/index").then(m => ({ default: m.AdminSettings })));
const AdminAnalytics = lazy(() => import("./admin/index").then(m => ({ default: m.AdminAnalytics })));
const AdminServices = lazy(() => import("./admin/index").then(m => ({ default: m.AdminServices })));
const AdminLocations = lazy(() => import("./admin/index").then(m => ({ default: m.AdminLocations })));
const AdminIndustries = lazy(() => import("./admin/index").then(m => ({ default: m.AdminIndustries })));
const AdminTestimonials = lazy(() => import("./admin/index").then(m => ({ default: m.AdminTestimonials })));
const AdminCaseStudies = lazy(() => import("./admin/index").then(m => ({ default: m.AdminCaseStudies })));
const AdminSEO = lazy(() => import("./admin/index").then(m => ({ default: m.AdminSEO })));
const AdminFAQs = lazy(() => import("./admin/index").then(m => ({ default: m.AdminFAQs })));
const AdminRedirects = lazy(() => import("./admin/index").then(m => ({ default: m.AdminRedirects })));
const AdminAnnouncements = lazy(() => import("./admin/index").then(m => ({ default: m.AdminAnnouncements })));
const AdminTeam = lazy(() => import("./admin/index").then(m => ({ default: m.AdminTeam })));
const AdminForms = lazy(() => import("./admin/index").then(m => ({ default: m.AdminForms })));
const AdminEmailTemplates = lazy(() => import("./admin/index").then(m => ({ default: m.AdminEmailTemplates })));
const AdminAuditLog = lazy(() => import("./admin/index").then(m => ({ default: m.AdminAuditLog })));

const AppContent = () => {
  const { settings, loading } = useCMSSettings();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><div className="w-8 h-8 rounded-full border-t-2 border-amber-500 animate-spin" /></div>;
  }

  const isAdminRoute = location.pathname.startsWith('/admin');
  const hasToken = !!localStorage.getItem('admin_token');

  if (settings?.maintenance_mode === 'true' && !isAdminRoute && !hasToken) {
    return <MaintenancePage />;
  }

  return (
    <Suspense fallback={<div style={{ minHeight: "40vh", paddingTop: 120 }} />}>
      <Routes>
        {/* Admin Routes — no public Layout wrapper */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="pages" element={<AdminPages />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="nav" element={<AdminNav />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="locations" element={<AdminLocations />} />
          <Route path="industries" element={<AdminIndustries />} />
          <Route path="case-studies" element={<AdminCaseStudies />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="seo" element={<AdminSEO />} />
          <Route path="faqs" element={<AdminFAQs />} />
          <Route path="redirects" element={<AdminRedirects />} />
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="forms" element={<AdminForms />} />
          <Route path="email-templates" element={<AdminEmailTemplates />} />
          <Route path="audit-log" element={<AdminAuditLog />} />
          <Route path="integrations" element={<AdminIntegrations />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="analytics" element={<AdminAnalytics />} />
        </Route>

        {/* Public Routes — wrapped in Layout (Navbar + Footer) */}
        <Route element={<Layout />}>
          <Route path="/" element={<Index />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:postSlug" element={<BlogPostPage />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/uae" element={<Uae />} />
          <Route path="/uk" element={<Uk />} />
          <Route path="/services" element={<Services />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/sitemap" element={<HTMLSitemap />} />
          <Route path="/services/web-development" element={<WebDevelopment />} />
          <Route path="/services/crm-development" element={<CrmDevelopment />} />
          <Route path="/services/erp-development" element={<ErpDevelopment />} />
          <Route path="/services/ai-agents" element={<AiAgents />} />
          <Route path="/services/ai-call-centre" element={<AiCallCentre />} />
          <Route path="/services/llm-integration" element={<LlmIntegration />} />
          <Route path="/services/rpa-automation" element={<RpaAutomation />} />
          <Route path="/services/app-development" element={<AppDevelopment />} />
          <Route path="/services/digital-marketing" element={<DigitalMarketing />} />
          <Route path="/services/brand-strategy" element={<BrandStrategy />} />
          <Route path="/services/shopify" element={<Shopify />} />
          <Route path="/services/custom-software" element={<CustomSoftware />} />
          <Route path="/services/graphic-design" element={<GraphicDesign />} />
          <Route path="/services/content-writing" element={<ContentWriting />} />
          <Route path="/services/wordpress" element={<WordPress />} />
          <Route path="/services/creative-services" element={<CreativeServices />} />
          <Route path="/services/seo-services" element={<SEOServices />} />
          <Route path="/services/seo" element={<Seo />} />
          <Route path="/services/digital-strategy" element={<DigitalStrategy />} />
          <Route path="/services/social-media" element={<SocialMedia />} />
          <Route path="/services/ecommerce" element={<ECommerce />} />
          <Route path="/services/ui-ux-design" element={<UIUXDesign />} />
          <Route path="/services/ui-ux" element={<UiUx />} />
          <Route path="/services/branding" element={<Branding />} />
          <Route path="/services/web-apps" element={<WebApps />} />
          <Route path="/services/mobile-apps" element={<MobileApps />} />
          <Route path="/services/api-development" element={<ApiDevelopment />} />
          <Route path="/services/cloud-devops" element={<CloudDevOps />} />
          <Route path="/services/integrations" element={<Integrations />} />
          <Route path="/services/rag-pipelines" element={<RagPipelines />} />
          <Route path="/services/analytics" element={<Analytics />} />
          <Route path="/services/ai-consulting" element={<AiConsulting />} />
          <Route path="/services/:serviceSlug/:locationSlug" element={<LocationServicePage />} />
          <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
          <Route path="/work" element={<WorkIndex />} />
          <Route path="/work/:slug" element={<WorkCaseStudy />} />
          <Route path="/case-studies" element={<Navigate to="/work" replace />} />
          <Route path="/case-studies/:slug" element={<WorkCaseStudy />} />
          <Route path="/portfolio" element={<Navigate to="/work" replace />} />
          <Route path="/industries" element={<IndustriesIndex />} />
          <Route path="/industries/legal" element={<Legal />} />
          <Route path="/industries/real-estate" element={<RealEstate />} />
          <Route path="/industries/healthcare" element={<Healthcare />} />
          <Route path="/industries/education" element={<Education />} />
          <Route path="/industries/manufacturing" element={<Manufacturing />} />
          <Route path="/products" element={<ProductsIndex />} />
          <Route path="/products/:productSlug" element={<ProductDetail />} />
          <Route path="/india" element={<IndiaIndex />} />
          <Route path="/india/mumbai" element={<Mumbai />} />
          <Route path="/get-quote" element={<Navigate to="/contact" replace />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/:countrySlug/:citySlug" element={<CityLandingPage />} />
          <Route path="/:countrySlug" element={<CountryLandingPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

const isDesktop = () =>
  typeof window !== "undefined" &&
  window.innerWidth >= 768 &&
  !("ontouchstart" in window) &&
  navigator.maxTouchPoints === 0;

const App = () => (
  <HelmetProvider>
    <CMSSettingsProvider>
      <BrowserRouter>
        <RouteEffects />
        <InjectIntegrations />
        {isDesktop() && (
          <Suspense fallback={null}>
            <CustomCursor />
            <MouseTrail />
          </Suspense>
        )}
        <AppContent />
        <CookieBanner />
      </BrowserRouter>
    </CMSSettingsProvider>
  </HelmetProvider>
);

export default App;
